import i18n from "i18next";
import { useEffect, useReducer } from "react";
import { initReactI18next } from "react-i18next";

import { I18nWrapper, ResourcesKey } from "types/i18n";

import { default as en } from "../en";
import { default as es } from "../es";

export type TranslationFn = typeof i18nWrapper.t;

export const DEFAULT_LANGUAGE: ResourcesKey = "en";

export const LANGUAGE_CHOICE_QUERY_PARAM = "lang";

export const MODAL_ROOT_ID = "translations-root";

export const LANGUAGE_LABELS: Record<ResourcesKey, string> = {
  en: "English",
  es: "Spanish",
};

export const SUPPORTED_LANGUAGES = Object.keys(
  LANGUAGE_LABELS
) as Array<ResourcesKey>;

const i18nWrapper: I18nWrapper = {
  t: i18n.t.bind(i18n),

  changeLanguage: async (lng: ResourcesKey) => {
    return await i18n.changeLanguage(lng);
  },

  getCurrentLanguage: () => {
    return i18n.language as ResourcesKey;
  },

  exists: (key: string) => {
    return i18n.exists(key);
  },

  getLanguages: () => {
    return Object.keys(i18n.options.resources || {}) as Array<ResourcesKey>;
  },
};

i18n.use(initReactI18next).init({
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: {
    escapeValue: false, // react already protects against xss => https://www.i18next.com/translation-function/interpolation#unescape
  },
  lng: DEFAULT_LANGUAGE, // main language choice; change this at-will using `changeLanguage`
  resources: {
    en,
    es,
  },
});

export const isSupportedLanguage = (
  value?: string | null
): value is ResourcesKey =>
  typeof value === "string" &&
  SUPPORTED_LANGUAGES.includes(value as ResourcesKey);

export const resolveLanguage = (value?: string | null): ResourcesKey =>
  isSupportedLanguage(value) ? value : DEFAULT_LANGUAGE;

export const getLanguageFromURL = () =>
  new URLSearchParams(window.location.search).get(LANGUAGE_CHOICE_QUERY_PARAM);

export const resolvePageLanguage = (
  isMultiLingualEnabled: boolean,
  value: string | null = getLanguageFromURL()
): ResourcesKey =>
  isMultiLingualEnabled ? resolveLanguage(value) : DEFAULT_LANGUAGE;

export const i18nWrapperInstance: I18nWrapper = i18nWrapper;

/**
 * Pass `subtitleText` (from `UiStateContext`'s `uiValues.subtitleText`) to
 * sync this shared i18next instance to the legacy Apply Now app's existing
 * subtitle-language toggle for as long as the caller is mounted, resetting
 * back to the default language on unmount. Only the screen that owns the
 * toggle's lifetime should pass it — other components rendered alongside it
 * should call `useLanguage()` with no argument so they just react to
 * whatever language is already active instead of each fighting to set it.
 */
export const useLanguage = (subtitleText?: string): I18nWrapper => {
  const [, rerender] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    i18n.on("languageChanged", rerender);

    return () => {
      i18n.off("languageChanged", rerender);
    };
  }, []);

  useEffect(() => {
    if (subtitleText === undefined) {
      return undefined;
    }

    i18nWrapper.changeLanguage(resolveLanguage(subtitleText));

    return () => {
      i18nWrapper.changeLanguage(DEFAULT_LANGUAGE);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtitleText]);

  return i18nWrapper;
};
